import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  Unsubscribe,
  QueryDocumentSnapshot,
  DocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { initFirebase } from '../lib/firebase';
import type { Market, TenantMarket, DayOfWeek, TenantWithMarketInfo } from '../types';
import { isTenantAbsent } from '../types';
import { getTenantById, type Tenant } from './tenant';
import { ensureFreshToken } from '../lib/ensure-fresh-token';
import { reportError } from '../lib/error-reporting';

/**
 * Convert Firestore document to Market
 */
function docToMarket(doc: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>): Market {
  const data = doc.data();
  if (!data) {
    throw new Error(`Market document ${doc.id} has no data`);
  }

  // Validate required fields to prevent crashes in the app
  if (!data.city || typeof data.city !== 'string') {
    console.error(`Market ${doc.id} has invalid city:`, data.city);
  }
  if (!data.openingTime || typeof data.openingTime !== 'string') {
    console.error(`Market ${doc.id} has invalid openingTime:`, data.openingTime);
  }
  if (!data.closingTime || typeof data.closingTime !== 'string') {
    console.error(`Market ${doc.id} has invalid closingTime:`, data.closingTime);
  }

  // Convert dayOfWeek: handle legacy format where Sunday was 0 (now should be 7 per ISO 8601)
  let dayOfWeek: DayOfWeek = 1; // Default to Monday
  if (typeof data.dayOfWeek === 'number') {
    if (data.dayOfWeek === 0) {
      // Legacy format: 0 = Sunday, convert to ISO 8601 format: 7 = Sunday
      dayOfWeek = 7;
      console.warn(`Market ${doc.id} has legacy dayOfWeek: 0 (Sunday), converting to 7`);
    } else if (data.dayOfWeek >= 1 && data.dayOfWeek <= 7) {
      dayOfWeek = data.dayOfWeek as DayOfWeek;
    } else {
      console.error(`Market ${doc.id} has invalid dayOfWeek:`, data.dayOfWeek);
    }
  } else {
    console.error(`Market ${doc.id} has non-numeric dayOfWeek:`, data.dayOfWeek);
  }

  return {
    id: doc.id,
    city: data.city || 'Marché inconnu',
    dayOfWeek,
    openingTime: data.openingTime || '08:00',
    closingTime: data.closingTime || '13:00',
    description: data.description || undefined,
    address: data.address || '',
    picture: data.picture || undefined,
    isActive: data.isActive ?? true,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
  };
}

/**
 * Convert Firestore document to TenantMarket
 */
function docToTenantMarket(doc: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>): TenantMarket {
  const data = doc.data();
  if (!data) {
    throw new Error(`TenantMarket document ${doc.id} has no data`);
  }
  return {
    id: doc.id,
    tenantId: data.tenantId,
    marketId: data.marketId,
    orderStartTime: data.orderStartTime || undefined,
    orderEndTime: data.orderEndTime || undefined,
    orderDeadlineHours: data.orderDeadlineHours != null ? data.orderDeadlineHours : undefined,
    isActive: data.isActive ?? true,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
  };
}

// ==================== MARKETS CRUD ====================

/**
 * Get all markets
 */
export async function listMarkets(): Promise<Market[]> {
  const { db } = initFirebase();
  const marketsQuery = query(
    collection(db, 'markets'),
    orderBy('city'),
    orderBy('dayOfWeek')
  );

  const snapshot = await getDocs(marketsQuery);
  return snapshot.docs.map(docToMarket);
}

/**
 * Get all active markets
 */
export async function listActiveMarkets(): Promise<Market[]> {
  const { db } = initFirebase();
  const marketsQuery = query(
    collection(db, 'markets'),
    where('isActive', '==', true),
    orderBy('city'),
    orderBy('dayOfWeek')
  );

  const snapshot = await getDocs(marketsQuery);
  return snapshot.docs.map(docToMarket);
}

/**
 * Get a market by ID
 */
export async function getMarketById(marketId: string): Promise<Market | null> {
  const { db } = initFirebase();
  const marketDoc = await getDoc(doc(db, 'markets', marketId));

  if (!marketDoc.exists()) {
    return null;
  }

  return docToMarket(marketDoc);
}

/**
 * Create a new market (superadmin only)
 */
export async function createMarket(
  market: Omit<Market, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  await ensureFreshToken();

  const { db } = initFirebase();
  const now = Timestamp.now();

  const docRef = await addDoc(collection(db, 'markets'), {
    city: market.city,
    dayOfWeek: market.dayOfWeek,
    openingTime: market.openingTime,
    closingTime: market.closingTime,
    description: market.description || null,
    address: market.address,
    picture: market.picture || null,
    isActive: market.isActive,
    createdAt: now,
    updatedAt: now,
  });

  return docRef.id;
}

/**
 * Update a market (superadmin only)
 */
export async function updateMarket(
  marketId: string,
  updates: Partial<Omit<Market, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<void> {
  await ensureFreshToken();

  const { db } = initFirebase();
  const marketRef = doc(db, 'markets', marketId);

  await updateDoc(marketRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete a market (superadmin only)
 */
export async function deleteMarket(marketId: string): Promise<void> {
  await ensureFreshToken();

  const { db } = initFirebase();
  await deleteDoc(doc(db, 'markets', marketId));
}

/**
 * Subscribe to markets changes
 */
export function onMarketsSnapshot(
  callback: (markets: Market[]) => void,
  onlyActive: boolean = false
): Unsubscribe {
  const { db } = initFirebase();

  let marketsQuery;
  if (onlyActive) {
    marketsQuery = query(
      collection(db, 'markets'),
      where('isActive', '==', true),
      orderBy('city'),
      orderBy('dayOfWeek')
    );
  } else {
    marketsQuery = query(
      collection(db, 'markets'),
      orderBy('city'),
      orderBy('dayOfWeek')
    );
  }

  return onSnapshot(
    marketsQuery,
    (snapshot) => {
      const markets = snapshot.docs.map(docToMarket);
      callback(markets);
    },
    (error) => {
      reportError(error, { context: 'onMarketsSnapshot', onlyActive });
    }
  );
}

// ==================== TENANT-MARKET ASSOCIATIONS ====================

/**
 * Get all tenant-market associations for a tenant
 */
export async function getTenantMarkets(tenantId: string): Promise<TenantMarket[]> {
  const { db } = initFirebase();
  const tenantMarketsQuery = query(
    collection(db, 'tenantMarkets'),
    where('tenantId', '==', tenantId),
    where('isActive', '==', true)
  );

  const snapshot = await getDocs(tenantMarketsQuery);
  return snapshot.docs.map(docToTenantMarket);
}

/**
 * Get all tenant-market associations for a market
 */
export async function getMarketTenants(marketId: string): Promise<TenantMarket[]> {
  const { db } = initFirebase();
  const marketTenantsQuery = query(
    collection(db, 'tenantMarkets'),
    where('marketId', '==', marketId),
    where('isActive', '==', true),
    orderBy('createdAt', 'asc')
  );

  const snapshot = await getDocs(marketTenantsQuery);
  return snapshot.docs.map(docToTenantMarket);
}

/**
 * Get markets with details for a tenant
 */
export async function getTenantMarketsWithDetails(
  tenantId: string
): Promise<(TenantMarket & { market: Market })[]> {
  const tenantMarkets = await getTenantMarkets(tenantId);

  const marketsWithDetails = await Promise.all(
    tenantMarkets.map(async (tm) => {
      const market = await getMarketById(tm.marketId);
      return market ? { ...tm, market } : null;
    })
  );

  return marketsWithDetails.filter(
    (m): m is TenantMarket & { market: Market } => m !== null
  );
}

/**
 * Get tenants with details for a market
 * Used by customer portal to show merchants at a market
 * Filters out inactive tenants only (absent tenants are included)
 */
export async function getMarketTenantsWithDetails(
  marketId: string
): Promise<(TenantMarket & { tenant: Tenant })[]> {
  const marketTenants = await getMarketTenants(marketId);

  const tenantsWithDetails = await Promise.all(
    marketTenants.map(async (tm) => {
      const tenant = await getTenantById(tm.tenantId);
      // Filter: must exist and be active (absent tenants are included)
      if (!tenant || !tenant.isActive) {
        return null;
      }
      return { ...tm, tenant };
    })
  );

  return tenantsWithDetails.filter(
    (t): t is TenantMarket & { tenant: Tenant } => t !== null
  );
}

/**
 * Associate a tenant with a market (superadmin only)
 */
export async function linkTenantToMarket(
  tenantId: string,
  marketId: string,
  options?: {
    orderStartTime?: string;
    orderEndTime?: string;
    orderDeadlineHours?: number;
  }
): Promise<string> {
  await ensureFreshToken();

  const { db } = initFirebase();
  const now = Timestamp.now();

  // Check if association already exists
  const existingQuery = query(
    collection(db, 'tenantMarkets'),
    where('tenantId', '==', tenantId),
    where('marketId', '==', marketId)
  );
  const existingSnapshot = await getDocs(existingQuery);

  if (!existingSnapshot.empty) {
    // Reactivate existing association
    const existingDoc = existingSnapshot.docs[0];
    await updateDoc(doc(db, 'tenantMarkets', existingDoc.id), {
      isActive: true,
      orderStartTime: options?.orderStartTime || null,
      orderEndTime: options?.orderEndTime || null,
      orderDeadlineHours: options?.orderDeadlineHours ?? null,
      updatedAt: now,
    });
    return existingDoc.id;
  }

  // Create new association
  const docRef = await addDoc(collection(db, 'tenantMarkets'), {
    tenantId,
    marketId,
    orderStartTime: options?.orderStartTime || null,
    orderEndTime: options?.orderEndTime || null,
    orderDeadlineHours: options?.orderDeadlineHours ?? null,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  });

  return docRef.id;
}

/**
 * Update a tenant-market association
 */
export async function updateTenantMarket(
  tenantMarketId: string,
  updates: Partial<Pick<TenantMarket, 'orderStartTime' | 'orderEndTime' | 'orderDeadlineHours' | 'isActive'>>
): Promise<void> {
  await ensureFreshToken();

  const { db } = initFirebase();
  const tenantMarketRef = doc(db, 'tenantMarkets', tenantMarketId);

  // Convert undefined values to deleteField() sentinel so Firestore removes the field
  const cleanUpdates: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(updates)) {
    cleanUpdates[key] = value === undefined ? deleteField() : value;
  }

  await updateDoc(tenantMarketRef, {
    ...cleanUpdates,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Remove (deactivate) a tenant-market association
 */
export async function unlinkTenantFromMarket(tenantMarketId: string): Promise<void> {
  await ensureFreshToken();

  const { db } = initFirebase();
  const tenantMarketRef = doc(db, 'tenantMarkets', tenantMarketId);

  await updateDoc(tenantMarketRef, {
    isActive: false,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Subscribe to tenant-market associations for a tenant
 */
export function onTenantMarketsSnapshot(
  tenantId: string,
  callback: (tenantMarkets: TenantMarket[]) => void
): Unsubscribe {
  const { db } = initFirebase();

  const tenantMarketsQuery = query(
    collection(db, 'tenantMarkets'),
    where('tenantId', '==', tenantId),
    where('isActive', '==', true)
  );

  return onSnapshot(
    tenantMarketsQuery,
    (snapshot) => {
      const tenantMarkets = snapshot.docs.map(docToTenantMarket);
      callback(tenantMarkets);
    },
    (error) => {
      reportError(error, { context: 'onTenantMarketsSnapshot', tenantId });
    }
  );
}

// ==================== MARKET DISPLAY (Cross-Selling) ====================

/**
 * Get tenants for display on market page (minimal projection for performance)
 * Includes absent tenants with isAbsent flag
 */
export async function getMarketTenantsForDisplay(
  marketId: string
): Promise<TenantWithMarketInfo[]> {
  const tenantsWithDetails = await getMarketTenantsWithDetails(marketId);

  return tenantsWithDetails.map((tm) => ({
    id: tm.tenant.id,
    name: tm.tenant.name,
    slug: tm.tenant.slug,
    activityTypes: tm.tenant.activityTypes || [],
    description: tm.tenant.description,
    isAbsent: isTenantAbsent(tm.tenant),
  }));
}

/**
 * Get other merchants on the same market (excluding the specified tenant)
 * Used for cross-sell CTA
 */
export async function getOtherMerchantsOnMarket(
  tenantId: string,
  marketId: string
): Promise<TenantWithMarketInfo[]> {
  const tenants = await getMarketTenantsForDisplay(marketId);
  return tenants.filter((t) => t.id !== tenantId);
}

/**
 * Get count of other merchants on the same market
 * Used for cross-sell CTA display
 */
export async function getOtherMerchantsCount(
  tenantId: string,
  marketId: string
): Promise<number> {
  const others = await getOtherMerchantsOnMarket(tenantId, marketId);
  return others.length;
}

/**
 * Subscribe to the set of market IDs that have at least one active tenant association
 */
export function onActiveMarketIdsSnapshot(
  callback: (marketIds: Set<string>) => void
): Unsubscribe {
  const { db } = initFirebase();

  const activeTenantsQuery = query(
    collection(db, 'tenantMarkets'),
    where('isActive', '==', true)
  );

  return onSnapshot(
    activeTenantsQuery,
    async (snapshot) => {
      // For each tenantMarket, verify that the tenant itself is also active.
      // tenantMarkets.isActive only reflects the market association; a tenant's
      // account can be deactivated (tenant.isActive=false) while the association
      // record stays active, which would show markets with 0 visible merchants.
      const results = await Promise.all(
        snapshot.docs.map(async (d: QueryDocumentSnapshot<DocumentData>) => {
          const data = d.data();
          const tenant = await getTenantById(data.tenantId as string);
          if (!tenant || !tenant.isActive) return null;
          return data.marketId as string;
        })
      );
      const marketIds = new Set<string>(results.filter((id): id is string => id !== null));
      callback(marketIds);
    },
    (error) => {
      reportError(error, { context: 'onActiveMarketIdsSnapshot' });
    }
  );
}

/**
 * Subscribe to market tenants for real-time display
 * Returns minimal tenant info for market page (includes absent tenants)
 */
export function onMarketTenantsForDisplaySnapshot(
  marketId: string,
  callback: (tenants: TenantWithMarketInfo[]) => void
): Unsubscribe {
  const { db } = initFirebase();

  const marketTenantsQuery = query(
    collection(db, 'tenantMarkets'),
    where('marketId', '==', marketId),
    where('isActive', '==', true),
    orderBy('createdAt', 'asc')
  );

  return onSnapshot(
    marketTenantsQuery,
    async (snapshot) => {
      const tenantMarkets = snapshot.docs.map(docToTenantMarket);

      // Fetch tenant details for each association
      const tenantsWithDetails = await Promise.all(
        tenantMarkets.map(async (tm) => {
          const tenant = await getTenantById(tm.tenantId);
          // Filter: must exist and be active (absent tenants are included)
          if (!tenant || !tenant.isActive) {
            return null;
          }
          return {
            id: tenant.id,
            name: tenant.name,
            slug: tenant.slug,
            activityTypes: tenant.activityTypes || [],
            description: tenant.description,
            phoneNumber: tenant.phoneNumber,
            profilePhoto: tenant.profilePhoto,
            isAbsent: isTenantAbsent(tenant),
          } as TenantWithMarketInfo;
        })
      );

      callback(tenantsWithDetails.filter((t): t is TenantWithMarketInfo => t !== null));
    },
    (error) => {
      reportError(error, { context: 'onMarketTenantsForDisplaySnapshot', marketId });
    }
  );
}
